import {
  CalendarDays,
  GraduationCap,
  HeartHandshake,
  Megaphone,
  PartyPopper,
  ScrollText,
} from "lucide-react";

// Must match CATEGORIES / PRIORITIES in the server's services/noticeService.js (and the DB CHECK constraints).
export const NOTICE_CATEGORIES = {
  general: { label: "General", icon: Megaphone, chip: "bg-slate-100 text-slate-700 dark:bg-slate-500/15 dark:text-slate-300" },
  policy: { label: "Policy", icon: ScrollText, chip: "bg-indigo-50 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300" },
  holiday: { label: "Holiday", icon: PartyPopper, chip: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300" },
  event: { label: "Event", icon: CalendarDays, chip: "bg-sky-50 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300" },
  training: { label: "Training", icon: GraduationCap, chip: "bg-violet-50 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300" },
  benefits: { label: "Benefits", icon: HeartHandshake, chip: "bg-pink-50 text-pink-700 dark:bg-pink-500/15 dark:text-pink-300" },
};

export const NOTICE_PRIORITIES = {
  normal: { label: "Normal", chip: "", accent: "border-l-slate-300 dark:border-l-slate-600" },
  important: {
    label: "Important",
    chip: "bg-amber-50 text-amber-800 ring-1 ring-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:ring-amber-500/30",
    accent: "border-l-amber-400",
  },
  urgent: {
    label: "Urgent",
    chip: "bg-red-50 text-red-700 ring-1 ring-red-200 dark:bg-red-500/15 dark:text-red-300 dark:ring-red-500/30",
    accent: "border-l-red-500",
  },
};

export const NOTICE_STATUS = {
  active: { label: "Live", chip: "bg-green-50 text-green-700 dark:bg-green-500/15 dark:text-green-300" },
  scheduled: { label: "Scheduled", chip: "bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300" },
  expired: { label: "Expired", chip: "bg-gray-100 text-gray-600 dark:bg-gray-500/15 dark:text-gray-400" },
};


const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** "just now", "5m ago", "3h ago", "Yesterday", "Mon 12 Oct" (or with the year when it isn't this year). */
export function formatNoticeTime(value) {
  if (!value) return "";
  const date = new Date(value);
  const diff = Date.now() - date.getTime();
  if (diff >= 0 && diff < MINUTE) return "just now";
  if (diff >= 0 && diff < HOUR) return `${Math.floor(diff / MINUTE)}m ago`;
  if (diff >= 0 && diff < DAY) return `${Math.floor(diff / HOUR)}h ago`;
  if (diff >= 0 && diff < 2 * DAY) return "Yesterday";
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    ...(sameYear ? {} : { year: "numeric" }),
  });
}

/** Full local date and time, e.g. "Fri, 9 Oct 2026, 14:30". */
export const formatNoticeDateTime = (value) =>
  value
    ? new Date(value).toLocaleString("en-GB", {
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

/** Value for an <input type="datetime-local"> in the browser's time zone. */
export function toLocalInputValue(value) {
  if (!value) return "";
  const date = new Date(value);
  const offset = date.getTimezoneOffset() * MINUTE;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

export const isImage = (mimeType) => /^image\/(png|jpe?g|gif|webp)$/.test(mimeType || "");
export const isPdf = (mimeType) => mimeType === "application/pdf";
/** Opens in the in-app viewer (images and PDFs); everything else downloads. */
export const isViewable = (mimeType) => isImage(mimeType) || isPdf(mimeType);

export const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** Notices are live until expiresAt; the list can be stale for a minute, so hide expired ones on render. */
export const isLive = (notice, now = Date.now()) =>
  new Date(notice.publishAt).getTime() <= now && (!notice.expiresAt || new Date(notice.expiresAt).getTime() > now);

export const needsAck = (notice) => notice.requiresAck && !notice.acknowledgedAt && notice.status === "active";

/** Short plain-text preview of the body (first line(s), collapsed whitespace). */
export const previewText = (body, max = 160) => {
  const text = (body || "").replace(/\s+/g, " ").trim();
  return text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;
};

const URL_PATTERN = /(https?:\/\/[^\s<]+[^\s<.,:;"')\]!?])/g;

/** Splits text into strings and { url } parts so links can be rendered as anchors (never as HTML). */
export function linkifyParts(text) {
  return (text || "").split(URL_PATTERN).map((part, index) => (index % 2 === 1 ? { url: part } : part));
}
